import React from 'react';
import {Box, Paper, Stack, Tooltip, Typography} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {Chart} from 'react-chartjs-2';
import {DistributionBin, FiveNumberSummary} from '../../../utils/distributionStats';

interface MetricDistributionPanelProps {
    /** CSS flex order within the Dashboard column. */
    order: number;
    title: string;
    /** Tooltip body (the "How to read this chart" content). */
    howToRead: React.ReactNode;
    /** Metric name woven into the Q1/Q3 tooltips, e.g. "Failure Exposure". */
    metricName: string;
    bins: DistributionBin[];
    /** Ascending-sorted values, for the empirical CDF line. */
    values: number[];
    stats: FiveNumberSummary;
    /** Label for the count cell, e.g. "Barriers" / "Challenged barriers". */
    countLabel: string;
    /** Label for the mean cell, e.g. "Mean F.Exp" / "Mean rate". */
    meanLabel: string;
    xAxisLabel: string;
    /** Per-bin bar fill / border colors. Return a constant for a neutral palette. */
    barColor: (bin: DistributionBin) => string;
    barBorder: (bin: DistributionBin) => string;
    /** Extra stat cells appended after Max (e.g. above-threshold counts). */
    extraStats?: React.ReactNode;
    /** Optional bar-color legend rendered under the chart. */
    colorLegend?: React.ReactNode;
}

const pct = (v: number): string => `${Math.round(v * 100)}%`;

/** Shared histogram + cumulative-% distribution panel used by the Dashboard's
 *  Demand Coverage, Effectiveness Rate and Failure Exposure panels. Renders the
 *  five-number-summary stat row, a bar (PDF) + line (CDF) chart, and an optional
 *  color legend. The caller owns the data memo and the bin/bar-color semantics. */
export const MetricDistributionPanel: React.FC<MetricDistributionPanelProps> = ({
    order,
    title,
    howToRead,
    metricName,
    bins,
    values,
    stats,
    countLabel,
    meanLabel,
    xAxisLabel,
    barColor,
    barBorder,
    extraStats,
    colorLegend,
}) => {
    const labels = bins.map((b) => `${Math.round(b.start * 100)}–${Math.round(b.end * 100)}%`);
    const cdfAtBinEnd = bins.map((b) => {
        const upTo = values.filter((v) => v <= b.end).length;
        return (upTo / stats.n) * 100;
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data: any = {
        labels,
        datasets: [
            {
                type: 'bar' as const,
                label: 'Barrier count (Probability Density Function)',
                data: bins.map((b) => b.count),
                backgroundColor: bins.map(barColor),
                borderColor: bins.map(barBorder),
                borderWidth: 1,
                yAxisID: 'yCount',
                order: 2,
            },
            {
                type: 'line' as const,
                label: 'Cumulative % (Cumulative Distribution Function)',
                data: cdfAtBinEnd,
                borderColor: '#1976d2',
                backgroundColor: '#1976d2',
                borderWidth: 2,
                pointRadius: 3,
                tension: 0.2,
                yAxisID: 'yPct',
                order: 1,
            },
        ],
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const options: any = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {mode: 'index' as const, intersect: false},
        plugins: {
            legend: {position: 'top' as const, labels: {boxWidth: 12, font: {size: 11}}},
            tooltip: {
                callbacks: {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    label: (ctx: any): string => {
                        if (ctx.dataset.type === 'bar') return `${ctx.parsed.y} barrier${ctx.parsed.y === 1 ? '' : 's'} in ${ctx.label}`;
                        return `${ctx.parsed.y.toFixed(0)}% of barriers ≤ ${Math.round(bins[ctx.dataIndex].end * 100)}%`;
                    },
                },
            },
        },
        scales: {
            x: {title: {display: true, text: xAxisLabel}, ticks: {font: {size: 10}}},
            yCount: {type: 'linear' as const, position: 'left' as const, title: {display: true, text: 'Barrier count'}, beginAtZero: true, grace: '5%', ticks: {precision: 0}},
            // max slightly above 100 so the CDF's 100% plateau (line + markers) clears the
            // top edge; stepSize keeps the top labeled tick at 100 with headroom above it.
            yPct: {type: 'linear' as const, position: 'right' as const, title: {display: true, text: 'Cumulative %'}, min: 0, max: 105, grid: {drawOnChartArea: false}, ticks: {stepSize: 20}},
        },
    };
    return (
        <Paper variant="outlined" sx={{order, mt: 1.5, mb: 1.5, px: 2, py: 1.5, display: 'flex', flexDirection: 'column'}}>
            <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 1}}>
                <Typography variant="subtitle2">{title}</Typography>
                <Tooltip
                    arrow
                    slotProps={{tooltip: {sx: {maxWidth: 480}}}}
                    title={
                        <Box sx={{fontSize: '0.7rem', lineHeight: 1.5}}>
                            <Typography variant="caption" sx={{fontWeight: 'bold', display: 'block', mb: 0.5}}>How to read this chart</Typography>
                            {howToRead}
                        </Box>
                    }
                >
                    <HelpOutlineIcon sx={{fontSize: 15, color: 'text.disabled', cursor: 'help'}} />
                </Tooltip>
            </Box>
            <Box sx={{display: 'flex', gap: 3, mb: 1.5, flexWrap: 'wrap'}}>
                <Box><Typography variant="caption" color="text.secondary">{countLabel}</Typography><Typography variant="body2" fontWeight="bold">{stats.n}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">{meanLabel}</Typography><Typography variant="body2" fontWeight="bold">{pct(stats.mean)}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Min</Typography><Typography variant="body2" fontWeight="bold">{pct(stats.min)}</Typography></Box>
                <Tooltip arrow title={`First quartile (25th percentile) — 25% of barriers sit at or below this ${metricName}`}>
                    <Box sx={{cursor: 'help'}}><Typography variant="caption" color="text.secondary">Q1</Typography><Typography variant="body2" fontWeight="bold">{pct(stats.q1)}</Typography></Box>
                </Tooltip>
                <Box><Typography variant="caption" color="text.secondary">Median</Typography><Typography variant="body2" fontWeight="bold">{pct(stats.median)}</Typography></Box>
                <Tooltip arrow title={`Third quartile (75th percentile) — 75% of barriers sit at or below this ${metricName}`}>
                    <Box sx={{cursor: 'help'}}><Typography variant="caption" color="text.secondary">Q3</Typography><Typography variant="body2" fontWeight="bold">{pct(stats.q3)}</Typography></Box>
                </Tooltip>
                <Box><Typography variant="caption" color="text.secondary">Max</Typography><Typography variant="body2" fontWeight="bold">{pct(stats.max)}</Typography></Box>
                {extraStats}
            </Box>
            <Box sx={{height: 280}}>
                <Chart type="bar" data={data} options={options} />
            </Box>
            {colorLegend && (
                <Stack direction="row" spacing={2} justifyContent="center" sx={{mt: 0.5}} flexWrap="wrap" useFlexGap>
                    {colorLegend}
                </Stack>
            )}
        </Paper>
    );
};
